import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import { CardElement as CardElementStripe } from '@stripe/react-stripe-js';

import { t } from '@web-solutions/module-localization';

import { useRemoteConfig } from '@web-solutions/base-app/hooks/use-remote-config';

import { SplitCardElement } from '../../stripe/SplitCardElement';

import { CardElement as CardElementSolidgate } from '../../solidgate';
import { CardElement as CardElementPaddle } from '../../paddle';
import { CardElement as CardElementTailoredpay } from '../../tailoredpay';
import { buildApplePayField, isApplePayEnabled } from '../../tailoredpay/apple-pay';

import { PaymentSystem } from '../../constants';
import { getPublicKeys } from '../../utils/storage';

import { CardElement as CardElementRecurly } from './recurly';

const CardElement = React.forwardRef(
  (
    {
      paymentSystem,
      product,
      options,
      className,
      onSubmit,
      onChange,
      onReady,
      onSuccess,
      onError,
      onApplePayClick,
      onApplePaySubmit,
      onGooglePayClick,
      onGooglePaySubmit,
      onUserInfoChange,
      onEvent,
      onWalletToken,
      inputStyles,
      labelStyles,
      notEmptyLabelStyles,
      paymentPopup
    },
    ref
  ) => {
    const publicKeys = getPublicKeys();
    const {
      stripeSplitCardForm,
      collectApplePayBillingData,
      requestWalletEmail,
      requestApplePayEmail,
      collectEmailAfterPayment,
      applePayMerchantName,
    } = useRemoteConfig();

    // `requestApplePayEmail` is the pre-rename key still configured on most
    // funnels; `requestWalletEmail` is `null` until one is migrated.
    const requestWalletEmailFlag = requestWalletEmail ?? requestApplePayEmail;

    const handleSubmit = useCallback(
      (formData) => {
        formData.method = 'card';
        onSubmit(formData);
      },
      [onSubmit]
    );

    // TailoredPay's Apple Pay button is drawn by Collect.js, not by us, so all we
    // supply is the config plus the container to mount into. That container is the
    // same slot every SDK-rendered wallet button uses, so the button lands where
    // it does for the other providers.
    const tailoredpayApplePay = useMemo(() => {
      const containerId = options?.applePayButtonParams?.containerId;

      // NOTE: `applePayButtonParams.enabled` is deliberately ignored. It means
      // "the app renders its own Apple Pay button, so the SDK must not" — which is
      // never true here, because for TailoredPay the SDK's button is the only one
      // there is. (It is false on stage and production, via APPLE_PAY_H2H.)
      //
      // One-time products get no wallet button either: upsells are charged to the
      // card on file through /nmi/one-time, so a wallet there would tokenize a
      // second card for nothing — and a cancelled sheet crashes Collect.js
      // internally (it reads `paymentMethod.network.toLowerCase()` unguarded).
      const isOneTime = Boolean(product?.isOneTimePurchase) || product?.period === 'ONETIME';

      if (
        paymentSystem !== PaymentSystem.TAILOREDPAY
        || !containerId
        || isOneTime
        || !isApplePayEnabled()
      ) {
        return null;
      }

      return buildApplePayField({
        selector: `#${containerId}`,
        product,
        // The payee shown in the sheet ("Pay <name>" and the recurring row). Comes
        // from remote config so it can be changed without a release; `core.appName`
        // is only the fallback for a config that has not set it.
        label: applePayMerchantName || t('core.appName'),
        buttonStyle: options?.applePayButtonParams?.color,
        collectBillingData: Boolean(collectApplePayBillingData),
        // Same condition Stripe uses: the email is only worth asking for when it
        // would otherwise be missing (deferred-email flow) or when we are already
        // collecting billing data.
        requestEmail: Boolean(requestWalletEmailFlag)
          && Boolean(collectEmailAfterPayment || collectApplePayBillingData),
      });
    }, [
      paymentSystem,
      options?.applePayButtonParams,
      product,
      collectApplePayBillingData,
      requestWalletEmailFlag,
      collectEmailAfterPayment,
      applePayMerchantName,
    ]);

    switch (paymentSystem) {
      case PaymentSystem.RECURLY:
        return (
          <CardElementRecurly
            ref={ref}
            product={product}
            options={options}
            className={className}
            onSubmit={handleSubmit}
            onChange={onChange}
            onReady={onReady}
            paymentPopup={paymentPopup}
          />
        );
      case PaymentSystem.STRIPE:
        return stripeSplitCardForm ? (
          <SplitCardElement
            ref={ref}
            options={options}
            className={className}
            onChange={onChange}
            onReady={onReady}
          />
        ) : (
          <CardElementStripe
            ref={ref}
            product={product}
            options={options}
            className={className}
            onSubmit={handleSubmit}
            onChange={onChange}
            onReady={onReady}
          />
        );
      case PaymentSystem.SOLIDGATE:
        return (
          <CardElementSolidgate
            ref={ref}
            product={product}
            options={options}
            className={className}
            onChange={onChange}
            onReady={onReady}
            onSubmit={handleSubmit}
            onSuccess={onSuccess}
            onError={onError}
            onApplePayClick={onApplePayClick}
            onApplePaySubmit={onApplePaySubmit}
            onGooglePayClick={onGooglePayClick}
            onGooglePaySubmit={onGooglePaySubmit}
            onUserInfoChange={onUserInfoChange}
            inputStyles={inputStyles}
            labelStyles={labelStyles}
            notEmptyLabelStyles={notEmptyLabelStyles}
          />
        );
      case PaymentSystem.TAILOREDPAY:
        return (
          <CardElementTailoredpay
            className={className}
            placeholders={options?.placeholders}
            // What is charged today — Collect.js needs it (plus currency and
            // country) to build its digital-wallet abstraction at configure time,
            // even though we never render a wallet button.
            price={
              product?.trialPriceAmount
              || product?.trial_price_amount
              || product?.amount
            }
            currency={product?.currency}
            applePay={tailoredpayApplePay}
            onChange={onChange}
            onReady={onReady}
            onError={onError}
            onWalletToken={onWalletToken}
          />
        );
      case PaymentSystem.PADDLE:
        return (
          <CardElementPaddle
            ref={ref}
            config={publicKeys?.paddle}
            product={product}
            options={options}
            className={className}
            onChange={onChange}
            onReady={onReady}
            onSuccess={onSuccess}
            onError={onError}
            onUserInfoChange={onUserInfoChange}
            onEvent={onEvent}
          />
        );
    }
    return null;
  }
);

CardElement.propTypes = {
  onSubmit: PropTypes.func,
  onChange: PropTypes.func,
  onReady: PropTypes.func,
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
  onApplePayClick: PropTypes.func,
  onApplePaySubmit: PropTypes.func,
  onGooglePayClick: PropTypes.func,
  onGooglePaySubmit: PropTypes.func,
  onUserInfoChange: PropTypes.func,
  onEvent: PropTypes.func,
  onWalletToken: PropTypes.func,
  className: PropTypes.string,
  options: PropTypes.object,
};

export default CardElement;
